import { useRef, useState } from 'react';
import { audioContext } from '@/audio/context';
import { Listener } from '@/speech/audio/listener';
import { openMicrophone, type AudioInput } from '@/speech/audio/sources';

/**
 * `?audiotest`: a diagnostic page for the iPhone crackle (issue #39). It plays the same voice line
 * the game uses under one audio setup per button, so a tester can tell which setup crackles. Tap
 * the buttons in order (the microphone state carries over), and mark each one clean or crackly.
 */
const MANIFEST = `${import.meta.env.BASE_URL}voice/manifest.json`;

interface Clip {
  url: string;
  start: number;
  end: number;
}

/** A 3-5 s line from the current lines sheet (its file name changes with every build). */
let clipPromise: Promise<Clip> | undefined;
function clip(): Promise<Clip> {
  clipPromise ??= fetch(MANIFEST)
    .then(
      (r) =>
        r.json() as Promise<{
          sheets: { file: string; clips: Record<string, { start: number; end: number }[]> }[];
        }>,
    )
    .then(({ sheets }) => {
      const sheet = sheets.find((s) => s.file.startsWith('lines-')) ?? sheets[0];
      if (!sheet) throw new Error('no voice sheet');
      // The earliest 3-5 s line, so the audio element needs little of the file.
      const span = Object.values(sheet.clips)
        .flat()
        .filter((c) => c.end - c.start > 3 && c.end - c.start < 5)
        .sort((x, y) => x.start - y.start)[0] ?? { start: 0, end: 4 };
      return { url: `${import.meta.env.BASE_URL}voice/${sheet.file}`, ...span };
    });
  return clipPromise;
}

type Rating = 'clean' | 'crackly';

interface Variant {
  id: string;
  label: string;
  run: (lab: Lab) => Promise<string>;
}

/** The audio state that carries over between steps. */
interface Lab {
  mic: AudioInput | null;
  listener: Listener | null;
  fresh: AudioContext | null;
}

const buffers = new WeakMap<AudioContext, Promise<AudioBuffer>>();
function sheet(context: AudioContext): Promise<AudioBuffer> {
  let b = buffers.get(context);
  if (!b) {
    b = clip()
      .then((c) => fetch(c.url))
      .then((r) => r.arrayBuffer())
      .then((data) => context.decodeAudioData(data));
    buffers.set(context, b);
  }
  return b;
}

async function playWebAudio(context: AudioContext): Promise<string> {
  if (context.state === 'suspended') await context.resume();
  const buffer = await sheet(context);
  const { start, end } = await clip();
  const source = context.createBufferSource();
  source.buffer = buffer;
  source.connect(context.destination);
  source.start(0, start, end - start);
  await new Promise((r) => {
    source.onended = r;
  });
  return `context ${String(context.sampleRate)} Hz, buffer ${String(buffer.sampleRate)} Hz, base latency ${(context.baseLatency * 1000).toFixed(0)} ms`;
}

async function playElement(): Promise<string> {
  const { url, start, end } = await clip();
  const audio = new Audio(url);
  audio.preload = 'auto';
  // Seeking works only once the metadata is in.
  await new Promise<void>((resolve, reject) => {
    audio.onloadedmetadata = () => {
      resolve();
    };
    audio.onerror = () => {
      reject(new Error('the audio element could not load the sheet'));
    };
  });
  audio.currentTime = start;
  await audio.play();
  const deadline = performance.now() + (end - start + 3) * 1000;
  await new Promise<void>((resolve) => {
    const timer = setInterval(() => {
      if (audio.currentTime >= end || performance.now() > deadline) {
        clearInterval(timer);
        audio.pause();
        resolve();
      }
    }, 30);
  });
  return 'HTML audio element';
}

async function micOn(lab: Lab, echoCancellation: boolean): Promise<string> {
  await micOff(lab);
  const context = audioContext();
  await Listener.prepare(context);
  if (echoCancellation) {
    lab.mic = await openMicrophone();
  } else {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      },
    });
    lab.mic = {
      stream,
      close: () => {
        stream.getTracks().forEach((t) => {
          t.stop();
        });
      },
    };
  }
  lab.listener = await Listener.start(lab.mic.stream, {}, context);
  const settings = lab.mic.stream.getAudioTracks()[0]?.getSettings();
  return `mic ${String(settings?.sampleRate ?? '?')} Hz, echo cancellation ${String(settings?.echoCancellation)}`;
}

async function micOff(lab: Lab) {
  await lab.listener?.close();
  lab.mic?.close();
  lab.listener = null;
  lab.mic = null;
}

const session = () =>
  (navigator as Navigator & { audioSession?: { type: string } }).audioSession?.type ?? 'n/a';

const VARIANTS: Variant[] = [
  {
    id: '1',
    label: '1. Web Audio, microphone off',
    run: () => playWebAudio(audioContext()),
  },
  { id: '2', label: '2. Audio element, microphone off', run: () => playElement() },
  {
    id: '3',
    label: '3. Web Audio, microphone on (as in the game)',
    run: async (lab) => {
      const mic = await micOn(lab, true);
      return `${mic}; ${await playWebAudio(audioContext())}`;
    },
  },
  {
    id: '4',
    label: '4. Web Audio started after the microphone (new audio context)',
    run: async (lab) => {
      lab.fresh ??= new AudioContext();
      return playWebAudio(lab.fresh);
    },
  },
  { id: '5', label: '5. Audio element, microphone on', run: () => playElement() },
  {
    id: '6',
    label: '6. Web Audio, microphone on without echo cancellation',
    run: async (lab) => {
      const mic = await micOn(lab, false);
      return `${mic}; ${await playWebAudio(audioContext())}`;
    },
  },
  {
    id: '7',
    label: '7. Web Audio, microphone switched off again',
    run: async (lab) => {
      await micOff(lab);
      return playWebAudio(audioContext());
    },
  },
];

export function AudioTest() {
  const lab = useRef<Lab>({ mic: null, listener: null, fresh: null });
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [ratings, setRatings] = useState<Record<string, Rating>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const run = async (v: Variant) => {
    setBusy(v.id);
    try {
      const note = await v.run(lab.current);
      setNotes((n) => ({ ...n, [v.id]: `${note}; session ${session()}` }));
    } catch (e) {
      setNotes((n) => ({ ...n, [v.id]: `error: ${String(e)}` }));
    } finally {
      setBusy(null);
    }
  };

  const summary = VARIANTS.map((v) => `${v.id}:${ratings[v.id] ?? '-'}`).join(' ');

  return (
    <main
      style={{
        padding: 16,
        maxWidth: 560,
        margin: '0 auto',
        fontFamily: 'system-ui',
        background: '#fff',
        color: '#222',
        minHeight: '100vh',
      }}
    >
      <h1 style={{ fontSize: 22 }}>Sound check</h1>
      <p>
        Tap each button in order and listen to the line. Then mark it clean or crackly. Allow the
        microphone when asked.
      </p>
      {VARIANTS.map((v) => (
        <section key={v.id} style={{ borderTop: '1px solid #ddd', padding: '12px 0' }}>
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => void run(v)}
            style={{ fontSize: 17, padding: '10px 14px', width: '100%', textAlign: 'left' }}
          >
            {busy === v.id ? 'Playing…' : v.label}
          </button>
          {notes[v.id] && (
            <p style={{ fontSize: 13, color: '#555', margin: '6px 0' }}>{notes[v.id]}</p>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            {(['clean', 'crackly'] as const).map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => {
                  setRatings((x) => ({ ...x, [v.id]: r }));
                }}
                style={{
                  flex: 1,
                  padding: 8,
                  fontSize: 15,
                  background:
                    ratings[v.id] === r ? (r === 'clean' ? '#cfeccf' : '#f5c9c9') : '#f2f2f2',
                }}
              >
                {r}
              </button>
            ))}
          </div>
        </section>
      ))}
      <p style={{ fontSize: 15, fontWeight: 600 }}>Result: {summary}</p>
      <p style={{ fontSize: 13, color: '#555' }}>{navigator.userAgent}</p>
    </main>
  );
}
